import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import type { ErrorStateDetails } from '#src/app/shared/utils/async';
import { isError, isLoaded } from '#src/app/shared/utils/async';

import type { ContributionsPageCombinedState, ContributionsPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { ContributionsPageDataContextProvider } from './pageData';
import { ContributionsPageStateContextProvider } from './pageState';
import { ContributionsPageTrackingContextProvider } from './trackingContext';

export type ErrorHandlerProps = {
	context: ErrorStateDetails<ContributionsPageCombinedState>;
};

type Props = {
	prefetchedData: ContributionsPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const ContributionsPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;
	const { subscription } = pageData.data;

	return (
		<ContributionsPageDataContextProvider pageData={pageData}>
			<ContributionsPageStateContextProvider subscription={subscription}>
				<ContributionsPageTrackingContextProvider>{children}</ContributionsPageTrackingContextProvider>
			</ContributionsPageStateContextProvider>
		</ContributionsPageDataContextProvider>
	);
};
